import React, { useState, useEffect, useRef } from 'react';
import { Calculator, DollarSign, Image, Video, Info } from 'lucide-react';
import {
  loadPricingConfig,
  calculatePromotionServiceFee,
  getConfigForFormat,
  formatPrice,
  getFormatDescription,
} from '../../../utils/promotionPricing';

const SLIDE_DURATION_PRESETS = [5, 10, 15, 20, 25, 30];

const PromotionEstimator = ({
  onPriceCalculated,
  displayFormat: externalDisplayFormat,
  slideDuration: externalSlideDuration,
  mediaType: externalMediaType,
  startDate: externalStartDate,
  endDate: externalEndDate,
  isFeatured: externalIsFeatured,
}) => {
  const [internalDisplayFormat, setInternalDisplayFormat] = useState('section');
  const [internalSlideDuration, setInternalSlideDuration] = useState(5);
  const [internalMediaType, setInternalMediaType] = useState('image');

  const displayFormat = externalDisplayFormat !== undefined ? externalDisplayFormat : internalDisplayFormat;
  const slideDuration = externalSlideDuration !== undefined ? externalSlideDuration : internalSlideDuration;
  const mediaType = externalMediaType !== undefined ? externalMediaType : internalMediaType;
  const startDate = externalStartDate !== undefined ? externalStartDate : null;
  const endDate = externalEndDate !== undefined ? externalEndDate : null;
  const isFeatured = externalIsFeatured !== undefined ? !!externalIsFeatured : false;

  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const onCalculatedRef = useRef(onPriceCalculated);
  useEffect(() => {
    onCalculatedRef.current = onPriceCalculated;
  }, [onPriceCalculated]);

  const canUseVideo = displayFormat === 'floating_left' || displayFormat === 'section';
  const effectiveMediaType = canUseVideo ? mediaType : 'image';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadPricingConfig();
      if (cancelled) return;
      const config = getConfigForFormat(displayFormat);
      const price = calculatePromotionServiceFee(config, {
        startDate: startDate || null,
        endDate: endDate || null,
        displayDurationSeconds: slideDuration,
        mediaType: effectiveMediaType,
        isFeatured,
      });
      if (cancelled) return;
      setCalculatedPrice(price);
      onCalculatedRef.current?.(price);
    })();
    return () => {
      cancelled = true;
    };
  }, [displayFormat, slideDuration, effectiveMediaType, startDate, endDate, isFeatured]);

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-4">
        <Calculator className="w-5 h-5 text-[#054625]" />
        <h3 className="text-lg font-bold text-gray-900">Estimateur de coût</h3>
      </div>

      <p className="text-xs text-gray-600 mb-4 rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
        <strong>Tarif plateforme</strong> : coût pour afficher votre promotion sur le site (emplacement, période,
        média). Ce n’est pas le prix du plat ni la réduction client — le montant définitif est calculé côté serveur
        au paiement (Stripe).
      </p>

      <div className="space-y-4">
        {/* Format d'affichage */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Format d'affichage
          </label>
          <div className="grid grid-cols-3 gap-3">
            {['floating_left', 'top_banner', 'section'].map((format) => (
              <button
                key={format}
                type="button"
                onClick={() => {
                  if (externalDisplayFormat === undefined) {
                    setInternalDisplayFormat(format);
                  }
                  if (format === 'top_banner' && externalMediaType === undefined) {
                    setInternalMediaType('image');
                  }
                }}
                disabled={externalDisplayFormat !== undefined}
                className={`p-3 rounded-lg border-2 transition-all ${
                  displayFormat === format
                    ? 'border-[#054625] bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${externalDisplayFormat !== undefined ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="text-sm font-medium text-gray-900">
                  {getFormatDescription(format)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {format === 'floating_left' && '7€ base'}
                  {format === 'top_banner' && '10€ base'}
                  {format === 'section' && '5€ base'}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Type de média */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type de média
          </label>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => {
                if (externalMediaType === undefined) {
                  setInternalMediaType('image');
                }
              }}
              disabled={(!canUseVideo && mediaType === 'video') || externalMediaType !== undefined}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-all ${
                effectiveMediaType === 'image'
                  ? 'border-[#054625] bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              } ${(!canUseVideo && mediaType === 'video') || externalMediaType !== undefined ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Image className="w-4 h-4" />
              <span>Image</span>
            </button>
            <button
              type="button"
              onClick={() => {
                if (externalMediaType === undefined) {
                  setInternalMediaType('video');
                }
              }}
              disabled={!canUseVideo || externalMediaType !== undefined}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-all ${
                effectiveMediaType === 'video'
                  ? 'border-[#054625] bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              } ${(!canUseVideo || externalMediaType !== undefined) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Video className="w-4 h-4" />
              <span>Vidéo (1-30s) +25%</span>
            </button>
          </div>
          {!canUseVideo && (
            <p className="text-xs text-gray-500 mt-1 flex items-center space-x-1">
              <Info className="w-3 h-3" />
              <span>La bande en haut ne supporte que les images</span>
            </p>
          )}
        </div>

        {/* Durée du slide */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Durée du slide: {slideDuration} secondes
          </label>
          <input
            type="range"
            min="5"
            max="30"
            value={slideDuration}
            onChange={(e) => {
              if (externalSlideDuration === undefined) {
                setInternalSlideDuration(parseInt(e.target.value, 10));
              }
            }}
            disabled={externalSlideDuration !== undefined}
            className={`w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer ${externalSlideDuration !== undefined ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>5s</span>
            <span>30s</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {SLIDE_DURATION_PRESETS.map((duration) => (
              <button
                key={duration}
                type="button"
                onClick={() => {
                  if (externalSlideDuration === undefined) {
                    setInternalSlideDuration(duration);
                  }
                }}
                disabled={externalSlideDuration !== undefined}
                className={`px-3 py-1 text-xs rounded ${
                  slideDuration === duration
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } ${externalSlideDuration !== undefined ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {duration}s
              </button>
            ))}
          </div>
        </div>

        {/* Calcul du prix */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-5 h-5 text-[#054625]" />
              <div>
                <div className="text-sm text-gray-600">Prix estimé (tarif plateforme)</div>
                <div className="text-2xl font-bold text-[#054625]">
                  {formatPrice(calculatedPrice)}
                </div>
                {effectiveMediaType === 'video' && (
                  <div className="text-xs text-gray-500 mt-1">
                    (Vidéo: +25 % sur le total)
                  </div>
                )}
              </div>
            </div>
            <div className="text-right text-xs text-gray-500 max-w-[10rem]">
              Période et options prises en compte si renseignées dans le formulaire (dates, à la une).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionEstimator;
